import { useRef, useState, useEffect, useCallback } from 'react';

// Extract poster from video's first frame using Canvas API
const extractPosterFromVideo = (videoUrl: string): Promise<string> => {
  return new Promise((resolve, reject) => {
    const video = document.createElement('video');
    video.crossOrigin = 'anonymous';
    video.muted = true;
    video.playsInline = true;
    video.preload = 'metadata';
    
    const cleanup = () => {
      video.removeEventListener('loadeddata', onLoaded);
      video.removeEventListener('error', onError);
      video.src = '';
      video.load();
    };

    const onLoaded = () => {
      try {
        const canvas = document.createElement('canvas');
        canvas.width = video.videoWidth || 640;
        canvas.height = video.videoHeight || 360;
        const ctx = canvas.getContext('2d');
        if (ctx) {
          ctx.drawImage(video, 0, 0, canvas.width, canvas.height);
          const dataUrl = canvas.toDataURL('image/jpeg', 0.8);
          cleanup();
          resolve(dataUrl);
        } else {
          cleanup();
          reject(new Error('Canvas context not available'));
        }
      } catch (e) {
        cleanup();
        reject(e);
      }
    };

    const onError = () => {
      cleanup();
      reject(new Error('Video load error'));
    };

    video.addEventListener('loadeddata', onLoaded);
    video.addEventListener('error', onError);
    video.src = videoUrl;
    video.currentTime = 0.1;
  });
};

interface LazyVideoProps {
  src: string;
  className?: string;
  containerClassName?: string;
}

const LazyVideo = ({ src, className = '', containerClassName = '' }: LazyVideoProps) => {
  const videoRef = useRef<HTMLVideoElement>(null);
  const containerRef = useRef<HTMLDivElement>(null);
  const [isInView, setIsInView] = useState(false);
  const [isVideoReady, setIsVideoReady] = useState(false);
  const [hasError, setHasError] = useState(false);
  const [poster, setPoster] = useState<string | null>(null);
  const [posterLoading, setPosterLoading] = useState(true);

  // Extract poster on mount
  useEffect(() => {
    let cancelled = false;
    
    extractPosterFromVideo(src)
      .then((dataUrl) => {
        if (!cancelled) {
          setPoster(dataUrl);
          setPosterLoading(false);
        }
      })
      .catch(() => {
        if (!cancelled) {
          setPosterLoading(false);
        }
      });

    return () => {
      cancelled = true;
    };
  }, [src]);

  // Intersection observer for lazy loading
  useEffect(() => {
    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting) {
          setIsInView(true);
          observer.unobserve(entry.target);
        }
      },
      { threshold: 0.1, rootMargin: '200px 0px' }
    );

    if (containerRef.current) {
      observer.observe(containerRef.current);
    }

    return () => observer.disconnect();
  }, []);

  const handleCanPlayThrough = useCallback(() => {
    setIsVideoReady(true);
    if (videoRef.current) {
      videoRef.current.play().catch(() => {});
    }
  }, []);

  return (
    <div ref={containerRef} className={`relative ${containerClassName}`}>
      {/* Poster image - shows immediately, fades out when video ready */}
      {poster && (
        <img
          src={poster}
          alt=""
          className={`absolute inset-0 w-full h-full object-cover transition-opacity duration-500 ${
            isVideoReady ? 'opacity-0 pointer-events-none' : 'opacity-100'
          }`}
        />
      )}

      {/* Loading skeleton - only if no poster yet */}
      {posterLoading && !poster && (
        <div className="absolute inset-0 bg-muted/20 animate-pulse rounded-lg" />
      )}

      {/* Video - fades in when ready */}
      {isInView && (
        <video
          ref={videoRef}
          autoPlay
          muted
          loop
          playsInline
          preload="auto"
          onCanPlayThrough={handleCanPlayThrough}
          onError={() => {
            setHasError(true);
          }}
          className={`w-full h-full object-cover transition-opacity duration-500 ${
            isVideoReady ? 'opacity-100' : 'opacity-0'
          } ${className}`}
        >
          <source src={src} type="video/mp4" />
        </video>
      )}

      {/* Error state */}
      {hasError && (
        <div className="absolute inset-0 bg-muted/10 flex flex-col items-center justify-center rounded-lg gap-2">
          <span className="text-muted-foreground text-sm">Video unavailable</span>
          <a
            href={src}
            target="_blank"
            rel="noopener noreferrer"
            className="text-xs text-primary underline hover:no-underline"
          >
            Open video
          </a>
        </div>
      )}
    </div>
  );
};

export default LazyVideo;
